/**
 * PrivacySettings — the GDPR "user area".
 *
 * Surfaces:
 *   - Active / missing / outdated consents per document type (with revoke)
 *   - Data export (JSON download)
 *   - Soft account deletion + restore
 *   - Link to open a memorial dispute
 *
 * Lives at /settings/privacy and is also linked from /settings.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLocale } from "@/lib/locale";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { Download, Trash2, RotateCcw, ShieldAlert, ChevronLeft, ExternalLink } from "lucide-react";

const DOC_NAV = {
    terms:              { path: "/legal/terms",           key: "legal.nav_terms" },
    privacy:            { path: "/legal/privacy",         key: "legal.nav_privacy" },
    biometric:          { path: "/legal/biometric",       key: "legal.nav_biometric" },
    ai_notice:          { path: "/legal/ai-notice",       key: "legal.nav_ai_notice" },
    rights_declaration: { path: "/legal/memorial-policy", key: "legal.nav_memorial" },
    memorial_policy:    { path: "/legal/memorial-policy", key: "legal.nav_memorial" },
};

export default function PrivacySettings() {
    const { t } = useLocale();
    const { logout, user } = useAuth();
    const nav = useNavigate();

    const [status, setStatus] = useState(null);
    const [accountState, setAccountState] = useState({ deleted_at: null, purge_after: null });
    const [busyConsent, setBusyConsent] = useState("");
    const [exportBusy, setExportBusy] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState("");
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [restoreBusy, setRestoreBusy] = useState(false);
    const [msg, setMsg] = useState("");

    const refresh = async () => {
        try {
            const { data } = await api.get("/consent/me");
            setStatus(data);
        } catch (_e) {
            setStatus(null);
        }
        try {
            const { data } = await api.get("/auth/me");
            setAccountState({
                deleted_at: data?.deleted_at || null,
                purge_after: data?.purge_after || null,
            });
        } catch (_e) { /* noop */ }
    };

    useEffect(() => { refresh(); }, []); // eslint-disable-line

    const rows = useMemo(() => {
        if (!status) return [];
        const req = status.required || [];
        return req.map((dt) => {
            const entry = status.latest?.[dt];
            const needs = (status.needs_reconsent || []).includes(dt);
            const missing = (status.missing || []).includes(dt);
            return {
                dt,
                entry,
                state: missing ? "missing" : needs ? "drift" : "active",
            };
        });
    }, [status]);

    const revoke = async (dt) => {
        setBusyConsent(dt);
        setMsg("");
        try {
            await api.post("/consent/revoke", { document_type: dt });
            setMsg(t("privacy.consent_revoked"));
            await refresh();
        } catch (_e) { /* noop */ }
        setBusyConsent("");
    };

    const exportData = async () => {
        setExportBusy(true);
        setMsg("");
        try {
            const { data } = await api.get("/users/me/export");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `otherside-export-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                URL.revokeObjectURL(url);
                a.remove();
            }, 1000);
            setMsg(t("privacy.export_done"));
        } catch (_e) { /* noop */ }
        setExportBusy(false);
    };

    const softDelete = async () => {
        if (confirmDelete.trim().toUpperCase() !== t("privacy.delete_confirm_word").toUpperCase()) return;
        setDeleteBusy(true);
        try {
            const { data } = await api.delete("/users/me");
            setAccountState({ deleted_at: data.deleted_at, purge_after: data.purge_after });
            setConfirmDelete("");
            setMsg(t("privacy.delete_done", { days: data.grace_days || 30 }));
        } catch (_e) { /* noop */ }
        setDeleteBusy(false);
    };

    const restoreAccount = async () => {
        setRestoreBusy(true);
        try {
            await api.post("/users/me/restore");
            setAccountState({ deleted_at: null, purge_after: null });
            setMsg(t("privacy.delete_restored"));
        } catch (_e) { /* noop */ }
        setRestoreBusy(false);
    };

    const purgeDate = accountState.purge_after
        ? new Date(accountState.purge_after).toLocaleDateString()
        : null;

    return (
        <div className="grain min-h-screen candle-halo">
            <Header minimal />
            <main className="max-w-3xl mx-auto px-6 md:px-12 pt-12 pb-24" data-testid="privacy-settings">
                <Link to="/settings" className="inline-flex items-center gap-2 text-ink-textMid hover:text-ink-textHigh text-sm mb-8 transition-colors">
                    <ChevronLeft size={14} /> {t("legal.back")}
                </Link>

                <div className="flex items-center gap-4 mb-10">
                    <BrandMark size={48} breathing={false} />
                    <div>
                        <p className="label-eyebrow text-ink-gold">{t("privacy.eyebrow")}</p>
                        <h1 className="h-serif text-4xl md:text-5xl mt-1">{t("privacy.title")}</h1>
                    </div>
                </div>

                <p className="text-ink-textMid text-lg mb-12 leading-relaxed max-w-xl">
                    {t("privacy.intro")}
                </p>

                {msg && (
                    <div className="mb-8 p-4 border-l-2 border-ink-gold bg-ink-surface/60 text-ink-textHigh italic" data-testid="privacy-message">
                        {msg}
                    </div>
                )}

                {/* ───── Consents ───── */}
                <section className="mb-16" data-testid="consent-status-section">
                    <h2 className="h-serif text-2xl mb-6">{t("privacy.section_consents")}</h2>
                    <div className="space-y-3">
                        {rows.map((r) => {
                            const navEntry = DOC_NAV[r.dt] || DOC_NAV.terms;
                            return (
                                <div key={r.dt} className="card-paper" data-testid={`consent-status-${r.dt}`}>
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <Link to={navEntry.path} className="font-serif text-ink-textHigh hover:text-ink-goldHi inline-flex items-center gap-1">
                                                {t(navEntry.key)} <ExternalLink size={12} />
                                            </Link>
                                            {r.entry && (
                                                <p className="text-xs text-ink-textMid mt-1">
                                                    {t("privacy.consent_accepted_at")}: {new Date(r.entry.timestamp).toLocaleString()}
                                                    {" · "}
                                                    {t("privacy.consent_version")}: {r.entry.document_version}
                                                    {r.entry.country ? ` · ${t("privacy.consent_country")}: ${r.entry.country}` : ""}
                                                </p>
                                            )}
                                            {r.state === "missing" && (
                                                <p className="text-xs text-ink-textMid italic mt-1">{t("privacy.consent_missing_hint")}</p>
                                            )}
                                            {r.state === "drift" && (
                                                <p className="text-xs text-ink-textMid italic mt-1">{t("privacy.consent_reconsent")}</p>
                                            )}
                                        </div>
                                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                            <span
                                                className={`text-xs uppercase tracking-widest px-2 py-1 rounded-sm ${
                                                    r.state === "active" ? "bg-ink-gold/20 text-ink-goldHi" :
                                                    r.state === "drift" ? "bg-amber-500/20 text-amber-200" :
                                                    "bg-ink-surface text-ink-textMid"
                                                }`}
                                                data-testid={`consent-state-${r.dt}`}
                                            >
                                                {r.state === "active" ? t("privacy.consent_active") :
                                                 r.state === "drift"  ? t("privacy.consent_drift")  :
                                                 t("privacy.consent_missing")}
                                            </span>
                                            {r.state === "active" && (
                                                <button
                                                    onClick={() => revoke(r.dt)}
                                                    disabled={busyConsent === r.dt}
                                                    className="btn-ghost text-xs"
                                                    data-testid={`consent-revoke-${r.dt}`}
                                                >
                                                    {busyConsent === r.dt ? t("privacy.consent_revoking") : t("privacy.consent_revoke")}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* ───── Data export ───── */}
                <section className="mb-16" data-testid="export-section">
                    <h2 className="h-serif text-2xl mb-4">{t("privacy.section_export")}</h2>
                    <p className="text-ink-textMid mb-5 leading-relaxed">{t("privacy.export_body")}</p>
                    <button
                        onClick={exportData}
                        disabled={exportBusy}
                        className="btn-primary inline-flex items-center gap-2"
                        data-testid="export-data-btn"
                    >
                        <Download size={16} /> {exportBusy ? t("privacy.export_busy") : t("privacy.export_cta")}
                    </button>
                </section>

                {/* ───── Soft deletion / restore ───── */}
                <section className="mb-16" data-testid="delete-section">
                    <h2 className="h-serif text-2xl mb-4">{t("privacy.section_delete")}</h2>
                    {accountState.deleted_at ? (
                        <div className="border-l-2 border-amber-400 bg-ink-surface/60 p-5" data-testid="delete-pending">
                            <p className="label-eyebrow text-amber-300 mb-2">{t("privacy.delete_pending")}</p>
                            <p className="text-ink-textMid mb-4">{t("privacy.delete_pending_body", { date: purgeDate || "—" })}</p>
                            <button
                                onClick={restoreAccount}
                                disabled={restoreBusy}
                                className="btn-primary inline-flex items-center gap-2"
                                data-testid="restore-account-btn"
                            >
                                <RotateCcw size={16} /> {restoreBusy ? t("privacy.consent_revoking") : t("privacy.delete_restore")}
                            </button>
                        </div>
                    ) : (
                        <>
                            <p className="text-ink-textMid mb-5 leading-relaxed">{t("privacy.delete_body")}</p>
                            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                                <input
                                    type="text"
                                    className="input-line sm:w-64"
                                    placeholder={t("privacy.delete_confirm")}
                                    value={confirmDelete}
                                    onChange={(e) => setConfirmDelete(e.target.value)}
                                    data-testid="delete-confirm-input"
                                />
                                <button
                                    onClick={softDelete}
                                    disabled={
                                        deleteBusy ||
                                        confirmDelete.trim().toUpperCase() !== t("privacy.delete_confirm_word").toUpperCase()
                                    }
                                    className="btn-ghost text-ink-danger inline-flex items-center gap-2 border-ink-danger/60 hover:!border-ink-danger"
                                    data-testid="delete-account-btn"
                                >
                                    <Trash2 size={16} /> {deleteBusy ? t("privacy.delete_busy") : t("privacy.delete_cta")}
                                </button>
                            </div>
                        </>
                    )}
                </section>

                {/* ───── Memorial dispute ───── */}
                <section data-testid="dispute-section">
                    <h2 className="h-serif text-2xl mb-4">{t("privacy.section_dispute")}</h2>
                    <p className="text-ink-textMid mb-5 leading-relaxed">{t("privacy.dispute_body")}</p>
                    <Link
                        to="/legal/memorial-dispute"
                        className="btn-ghost inline-flex items-center gap-2"
                        data-testid="open-dispute-btn"
                    >
                        <ShieldAlert size={16} /> {t("privacy.dispute_open")}
                    </Link>
                </section>
            </main>
        </div>
    );
}
